/**
 * Karbon360° v2 — CBAM shared reference data + small bits for the installations
 * and goods screens.
 *
 * Sectors, aggregated goods categories and production routes follow Annex I of
 * Regulation (EU) 2023/956 as mapped by Table 1 of IR (EU) 2025/2547. Only the
 * STRUCTURE lives here — default values, benchmarks and chargeability are
 * resolved by the server (/api/cbam/cn-lookup), so a regulation update lands in
 * one place and the client never shows a stale number.
 */
import React from 'react';

/**
 * The API serialises DECIMAL as string — normalise before display/compare.
 * null / undefined / '' / anything non-numeric all land on null, never NaN,
 * so `fmt(num(v))` renders a dash instead of leaking NaN into the UI.
 */
export const num = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v));

/* The definitive period starts 1 Jan 2026 — never offer earlier years. */
const THIS_YEAR = new Date().getFullYear();
export const CBAM_BASE_YEAR = Math.max(2026, THIS_YEAR);
export const CBAM_YEARS = [CBAM_BASE_YEAR - 1, CBAM_BASE_YEAR, CBAM_BASE_YEAR + 1, CBAM_BASE_YEAR + 2]
  .filter((y) => y >= 2026);

export const SECTORS = [
  { value: 'cement', label: 'Cement' },
  { value: 'fertilisers', label: 'Fertilisers' },
  { value: 'iron_steel', label: 'Iron & steel' },
  { value: 'aluminium', label: 'Aluminium' },
  { value: 'hydrogen', label: 'Hydrogen' },
  { value: 'electricity', label: 'Electricity' },
];

export const sectorLabel = (v) => (SECTORS.find((s) => s.value === v) || {}).label || v;

/**
 * Server quality vocabulary (actual | mixed | defaults) → badge tone. The
 * verdict itself always comes from /api/cbam/summary — the service is the one
 * place the rule lives — so the screens share only this tone mapping and never
 * re-derive the label. Anything short of 'actual' warns.
 */
export const QUALITY_TONE = { actual: 'ok', mixed: 'warn', defaults: 'warn' };

/** Aggregated goods categories per sector — Table 1, point 2, Annex I of IR (EU) 2025/2547. */
export const GOODS_CATEGORIES = {
  cement: ['Calcined clay', 'Cement clinker', 'Cement', 'Aluminous cement'],
  fertilisers: ['Nitric acid', 'Ammonia', 'Urea', 'Mixed fertilisers'],
  iron_steel: [
    'Sintered Ore', 'Pig iron', 'FeMn (ferro-manganese)', 'FeCr (ferro-chromium)',
    'FeNi (ferro-nickel)', 'DRI (direct reduced iron)', 'Crude steel', 'Iron or steel products',
  ],
  aluminium: ['Unwrought aluminium', 'Aluminium products'],
  hydrogen: ['Hydrogen'],
  electricity: ['Electricity'],
};

/**
 * Production routes in the letters the default-value and benchmark tables use
 * (IR 2025/2621 / 2025/2620). The i18n gloss is what the operator actually
 * recognises — "K" alone means nothing to a doors factory.
 */
export const ROUTES = ['K', 'L', 'C', 'D', 'E'];

/**
 * Countries offered where a country drives a server-side default-value lookup.
 * `value` is what /api/cbam/cn-lookup expects as ?country= (ISO2); the two
 * sentinels never reach the lookup: 'other' means the fallback table applies,
 * 'unknown' means the punitive Annex IV value applies.
 */
export const PRECURSOR_COUNTRIES = [
  { value: 'BA', sq: 'Bosnjë e Hercegovinë', en: 'Bosnia and Herzegovina' },
  { value: 'RS', sq: 'Serbi', en: 'Serbia' },
  { value: 'ME', sq: 'Mal i Zi', en: 'Montenegro' },
  { value: 'MK', sq: 'Maqedoni e Veriut', en: 'North Macedonia' },
  { value: 'AL', sq: 'Shqipëri', en: 'Albania' },
  { value: 'XK', sq: 'Kosovë', en: 'Kosovo' },
  { value: 'TR', sq: 'Turqi', en: 'Türkiye' },
  { value: 'CN', sq: 'Kinë', en: 'China' },
  { value: 'EG', sq: 'Egjipt', en: 'Egypt' },
  { value: 'other', sq: 'Vend tjetër (tabela fallback)', en: 'Other country (fallback table)' },
  { value: 'unknown', sq: 'Origjinë e panjohur', en: 'Unknown origin' },
];

/**
 * Buildings store the country as free text (often Albanian — "Kosovë"), while
 * the lookup endpoint and the carbon-price hint want an ISO2 code. Pattern
 * match rather than table-lookup so both spellings land.
 */
const ISO_PATTERNS = [
  ['BA', /bosni|bosnj/],
  ['XK', /kosov/],
  ['RS', /serb/],
  ['MK', /maqedon|macedon/],
  ['ME', /mal i zi|montenegro/],
  ['AL', /shqip|alban/],
  ['TR', /turq|türk|turk/],
  ['HR', /kroaci|croat/],
];

export function isoFromCountry(name) {
  if (!name) return null;
  const s = String(name).trim();
  if (/^[A-Z]{2}$/.test(s)) return s;
  const low = s.toLowerCase();
  const hit = ISO_PATTERNS.find(([, re]) => re.test(low));
  return hit ? hit[0] : null;
}

/**
 * Western Balkans have no carbon price in force today (Türkiye's ETS is still a
 * pilot, so it stays off this list — only an EFFECTIVELY PAID price deducts).
 * Drives the "X has no carbon price today — leave 0" hint, nothing more; the
 * user can always type a value if that changes.
 */
export const NO_CARBON_PRICE = new Set(['BA', 'XK', 'RS', 'MK', 'ME', 'AL']);

/* ── tiny shared bits ─────────────────────────────────────────────────── */

/** The v2 kit has no checkbox primitive yet; both CBAM screens need one. */
export function CheckRow({ checked, onChange, label, hint, style }) {
  return (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--n800)', cursor: 'pointer', ...style }}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 2, accentColor: 'var(--brand-700)' }}
      />
      <span>
        {label}
        {hint && <span style={{ display: 'block', fontSize: 12, color: 'var(--n500)', fontWeight: 400 }}>{hint}</span>}
      </span>
    </label>
  );
}
